/** Iter 639 — vendor panel split · marketing blocks: hero, value props,
 * invite card, FAQ (moved verbatim from pages/PartnerProgramPage.jsx). */
import React from "react";
import { Loader2, Handshake, ShieldCheck, Wallet, CheckCircle2, Building2 } from "lucide-react";
import HowItWorksButton from "@/components/common/HowItWorksButton";

export function Hero() {
  return (
    <div className="relative mt-6 overflow-hidden rounded-3xl bg-gradient-to-br from-[color:var(--brand-blue)] via-[color:var(--brand-blue)] to-slate-900 p-8 md:p-12 text-white">
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-xs font-bold uppercase tracking-widest">
          <Handshake className="w-3.5 h-3.5" /> Partner Program
        </div>
        <h1 className="mt-4 font-display text-3xl md:text-5xl font-black leading-tight" data-testid="partner-hero-title">
          Grow your business with Allsale.
        </h1>
        <p className="mt-3 text-white/80 leading-relaxed max-w-xl">
          A verified home for Kiwi dealers, tradies and specialty stores — with a branded seller
          page, direct payouts and priority onboarding.
        </p>
        {/* Iter-643 — vendor how-to video on the marketing hero. */}
        <div className="mt-5">
          <HowItWorksButton video="vendor" />
        </div>
      </div>
      <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-white/5 pointer-events-none" aria-hidden />
      <div className="absolute -right-6 -top-6 w-40 h-40 rounded-full bg-white/5 pointer-events-none" aria-hidden />
    </div>
  );
}

export function ValueProps() {
  const items = [
    {
      icon: Building2,
      title: "Verified seller page",
      body: "Your logo, licence and WhatsApp on a branded profile buyers can trust.",
    },
    {
      icon: Wallet,
      title: "Direct payouts",
      body: "Get paid straight to your bank via Stripe Connect — no waiting.",
    },
    {
      icon: ShieldCheck,
      title: "KYC-backed trust",
      body: "One-time ID check unlocks the 🏢 Verified Business badge on every ad.",
    },
  ];
  return (
    <ul className="space-y-4 text-sm">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <li key={it.title} className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center shrink-0">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold text-slate-900">{it.title}</div>
              <div className="text-slate-500">{it.body}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function InviteCard({ v }) {
  const { invite, inviteQ, inviteExpired: expired } = v;
  const loading = inviteQ.isLoading;
  const error = inviteQ.error;
  if (loading) {
    return (
      <div className="rounded-2xl bg-white shadow-card p-5 flex items-center gap-3" data-testid="partner-invite-loading">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        <span className="text-sm text-slate-500">Checking your invite…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-2xl bg-white shadow-card p-5 border border-red-100" data-testid="partner-invite-error">
        <div className="text-sm font-bold text-red-600">Invite link not found</div>
        <div className="mt-1 text-xs text-slate-500">
          The link may have expired. You can still apply below and we&apos;ll get you set up.
        </div>
      </div>
    );
  }
  if (!invite) return null;
  return (
    <div className="rounded-2xl bg-white shadow-card p-5 border border-emerald-100" data-testid="partner-invite-card">
      <div className="inline-flex items-center gap-2 text-emerald-600 text-xs font-bold uppercase tracking-widest">
        <CheckCircle2 className="w-3.5 h-3.5" /> Invite verified
      </div>
      <div className="mt-2 text-sm text-slate-900 font-semibold" data-testid="partner-invite-business">
        {invite.business_name || invite.email || "Welcome"}
      </div>
      {invite.invited_by && (
        <div className="text-xs text-slate-500">Invited by {invite.invited_by}</div>
      )}
      {expired && (
        <div className="mt-2 text-xs text-amber-600 font-semibold" data-testid="partner-invite-expired">
          This invite has already been used — contact support if you need help.
        </div>
      )}
    </div>
  );
}

const FAQS = [
  {
    q: "How much does it cost to become a partner?",
    a: "Joining is free. You only pay for premium listing placements or featured badges you choose to buy — pay-as-you-grow.",
  },
  {
    q: "How long does verification take?",
    a: "Most partners are verified within one business day. Complex documents (overseas ID, custom company structures) can take 2–3 days.",
  },
  {
    q: "Do I need an NZBN?",
    a: "Yes — NZBN, business phone and business address are all required so we can verify you and pay you out. Sole traders can register for a free NZBN in a few minutes at nzbn.govt.nz.",
  },
  {
    q: "How do payouts work?",
    a: "Stripe pays you directly to your NZ bank account. Allsale never touches your funds and never sees your bank details.",
  },
];

export function FaqSection() {
  return (
    <section className="mt-14" data-testid="partner-faq">
      <h2 className="font-display text-2xl md:text-3xl font-black text-slate-900">Partner Program FAQ</h2>
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {FAQS.map((f) => (
          <details
            key={f.q}
            className="rounded-2xl bg-white shadow-card p-5 group"
            data-testid={`partner-faq-item-${f.q.length}`}
          >
            <summary className="font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
              {f.q}
              <span className="text-slate-400 group-open:rotate-45 transition-transform">+</span>
            </summary>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
